import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { injectIntl, FormattedMessage, InjectedIntlProps } from 'react-intl'
import { Form } from 'antd'
import DropdownInput from 'components/DropdownInput'
import Button from 'components/Button'
import Input from 'components/Input'
import Icon from 'components/Icon'
import { selectCurrentZone } from 'store/global'
import messages from './messages'
import cx from 'classnames'
import styles from './GridActions.scss'

interface SearchableField {
  value: string
  label: string
}

interface Column {
  value: string
  label: string
}

interface SearchChip {
  label: string
  value: string
}

interface GridActionsProps extends InjectedIntlProps {
  search: string
  setSearch: (search: string) => void
  searchFields: string[]
  setSearchFields: (fields: string[]) => void
  withSearch?: boolean
  searchableFields?: SearchableField[]
  onFieldsChange?: (fields: string[]) => void
  allColumns?: Column[]
  displayedColumns?: string[]
  onColumnsChange: (columns: Column[]) => void
  onSearch?: () => void
  withShare?: boolean
  onShare?: () => void
  isShareDisabled?: boolean
  filters?: {
    search?: string
    [key: string]: any
  }
  isReducedSearchSize?: boolean
  removeColumnsPicker?: boolean
  children?: React.ReactNode
}

function GridActions({
  intl,
  search,
  setSearch,
  searchFields,
  setSearchFields,
  withSearch = true,
  searchableFields = [],
  onFieldsChange = () => {},
  allColumns = [],
  displayedColumns = [],
  onColumnsChange,
  onSearch = () => {},
  withShare = false,
  onShare = () => {},
  isShareDisabled = false,
  filters = {},
  isReducedSearchSize,
  removeColumnsPicker = false,
  children,
}: GridActionsProps) {
  const currentZone = useSelector(selectCurrentZone)

  const { search: activeSearch = '' } = filters

  const [searchChips, setSearchChips] = useState<SearchChip[]>([])
  const [didClearSearch, setDidClearSearch] = useState(false)

  const resetSearchChips = () => {
    setSearchChips([])
    setSearchFields(searchableFields.map(field => field.value))
  }

  useEffect(() => {
    setSearch('')
    resetSearchChips()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentZone])

  useEffect(() => {
    configureSearchFields(searchableFields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (search !== activeSearch) onSearch()
  }

  const configureSearchFields = (selections: SearchableField[]) => {
    if (selections.length === 0) return

    const selectionsMap = selections.reduce(
      (currentSelections, selection) => ({
        searchFields: [...currentSelections.searchFields, selection.value],
        searchChips: [...currentSelections.searchChips, { label: selection.label, value: selection.value }],
      }),
      { searchFields: [] as string[], searchChips: [] as SearchChip[] },
    )

    setSearchFields(selectionsMap.searchFields)

    const areAllFieldsSelected =
      searchableFields.length === selectionsMap.searchChips.length && searchableFields.length > 0

    setSearchChips(areAllFieldsSelected ? [] : selectionsMap.searchChips)
  }

  const onRemoveChip = (clickedChip: SearchChip) => {
    if (searchChips.length === 1) {
      resetSearchChips()
      return
    }

    const searchFieldIndex = searchFields.findIndex(field => field === clickedChip.value)
    const nextSearchFields = [...searchFields]
    nextSearchFields.splice(searchFieldIndex, 1)
    setSearchFields(nextSearchFields)
    setSearchChips(searchChips.filter(chip => chip.value !== clickedChip.value))
  }

  useEffect(() => {
    onFieldsChange(searchFields)
  }, [searchFields, onFieldsChange])

  const handleSearchClear = () => {
    setSearch('')
    resetSearchChips()
    setDidClearSearch(true)
  }

  useEffect(() => {
    if (didClearSearch) {
      onSearch()
      setDidClearSearch(false)
    }
  }, [didClearSearch, onSearch])

  const renderColumnsButton = () => (
    <Button buttonType='secondary' className={styles.action}>
      <Icon name='columns' size='regular'>
        <FormattedMessage {...(messages.actions as any)?.columns} />
      </Icon>
    </Button>
  )

  const renderSearchFieldsButton = () => (
    <Button>
      <Icon name='columns' size='regular' />
    </Button>
  )

  return (
    <div className={styles.actions}>
      {withSearch && (
        <div className={styles.search}>
          <Form onFinish={onSearchSubmit}>
            <Input
              type='search'
              icon='search'
              prefix={intl.formatMessage((messages.search as any)?.prefix)}
              searchChips={searchChips}
              onRemoveChip={onRemoveChip}
              placeholder={intl.formatMessage((messages.search as any)?.placeholder)}
              value={search}
              onClear={handleSearchClear}
              onChange={(nextSearch: string) => setSearch(nextSearch)}
              className={cx(styles.searchInput, {
                [styles.withOpenMap]: isReducedSearchSize,
              })}
            />
          </Form>
          <DropdownInput
            hasSelectAll={false}
            disableSearch
            options={searchableFields}
            selections={searchableFields.filter(({ value }) => searchFields.includes(value))}
            onChange={(selections) => configureSearchFields(selections)}
            onToggle={() => onFieldsChange(searchFields)}
            buttonRenderer={renderSearchFieldsButton}
            className={styles.dropdown}
          />
        </div>
      )}
      {!removeColumnsPicker && (
        <DropdownInput
          hasSelectAll
          disableSearch
          options={allColumns.map(col => ({ value: col.value, label: col.label }))}
          selections={allColumns.filter(({ value }) => displayedColumns.includes(value)).map(col => ({ value: col.value, label: col.label }))}
          onChange={onColumnsChange}
          buttonRenderer={renderColumnsButton}
        />
      )}
      {withShare && (
        <Button buttonType='secondary' onClick={onShare} className={styles.share} disabled={isShareDisabled}>
          <Icon name='share' size='regular'>
            <FormattedMessage {...(messages.actions as any)?.share} />
          </Icon>
        </Button>
      )}
      {children}
    </div>
  )
}

export default injectIntl(GridActions)

